import React, { useState } from "react";
import { Button, ButtonVariants, Devices } from "@app/shared";
import { Input, InputRow, ErrorAlert } from "../../../../shared";
import { Icons } from "../../../../shared";
import keyInPackageImage from "../../../../../assets/images/key-in-package.jpg";

interface DeviceProvisionStepProps {
    facilityId: string;
    facilityName?: string;
    groupName: string;
    onNext: (deviceId: string | undefined) => void;
    onBack: () => void;
}

export const DeviceProvisionStep: React.FC<DeviceProvisionStepProps> = ({ facilityId, facilityName, groupName, onNext, onBack }) => {
    const [deviceKey, setDeviceKey] = useState<string>("");
    const [error, setError] = useState<string>();
    const [isProvisioning, setIsProvisioning] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);

    const handleProvisionDevice = async () => {
        if (!deviceKey.trim()) {
            setError("Please enter a device key");
            return;
        }

        setIsProvisioning(true);
        setError(undefined);
        try {
            // Lookup the device
            const device = await Devices.getUnassociatedDevice(deviceKey.trim());
            if (!device) {
                return;
            }
            // Associate device with facility
            await Devices.associateDevice(device.id, facilityId);
            onNext(device.id);
        } catch (err) {
            console.error("Error provisioning device:", err);
            setError(`Unable to find device with key "${deviceKey.trim()}". Please check the device key and try again.`);
        } finally {
            setIsProvisioning(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && deviceKey.trim() && !isProvisioning) {
            handleProvisionDevice();
        }
    };

    const handleImageClick = () => {
        setShowImageModal(true);
    };

    const handleCloseModal = () => {
        setShowImageModal(false);
    };

    const handleModalBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setShowImageModal(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-primary-darkened rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icons.Video className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">Add Device</h2>
                <p className="text-neutral-600 mb-4">Enter the device key from the back of your device to link it to this location</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                        <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                        <span className="text-neutral-700 font-medium">Group: {groupName}</span>
                    </div>
                    {facilityName && (
                        <div className="inline-flex items-center px-4 py-2 bg-white border border-warmGray-200 rounded-full text-sm shadow-sm">
                            <div className="w-2 h-2 bg-brand-primary rounded-full mr-2"></div>
                            <span className="text-neutral-700 font-medium">Location: {facilityName}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-warmGray-100 p-8 mb-8">
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Device Information</h3>
                        <ErrorAlert errors={error} />
                        <InputRow>
                            <Input label="Device Key" value={deviceKey} onChange={(e) => setDeviceKey(e.target.value)} onKeyDown={handleKeyPress} placeholder="Enter device key (e.g., ABC123DEF456)" disabled={isProvisioning} autoFocus />
                        </InputRow>
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-r from-warmGray-50 to-white border border-warmGray-100 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-6">
                    <div className="flex items-start flex-1">
                        <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-0.5">
                            <Icons.Info className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h4 className="font-semibold text-neutral-900 mb-3">How to find your device key</h4>
                            <ul className="text-sm text-neutral-600 space-y-2 leading-relaxed">
                                <li className="flex items-start">
                                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                    Look on the back of your package
                                </li>
                                <li className="flex items-start">
                                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mr-3 mt-2 flex-shrink-0"></div>
                                    The device key is typically a series of letters and numbers
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <button onClick={handleImageClick} className="group h-full flex flex-col items-center justify-center cursor-pointer border-none hover:opacity-90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-opacity-50 rounded-xl p-2" aria-label="Click to enlarge image">
                            <div className="relative rounded-lg border-2 border-warmGray-200 group-hover:border-brand-primary transition-colors duration-200 shadow-sm">
                                <img src={keyInPackageImage} alt="Device key location on package - click to enlarge" className="w-32 h-24 object-cover group-hover:scale-105 transition-transform duration-200" />
                            </div>
                            <div className="text-xs text-warmGray-600 group-hover:text-brand-primary font-medium mt-2 transition-colors duration-200">Click to enlarge</div>
                        </button>
                    </div>
                </div>
            </div>

            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={handleModalBackdropClick}>
                    <div className="relative max-w-4xl max-h-full">
                        <img src={keyInPackageImage} alt="Device key location on package" className="max-w-full max-h-full object-contain rounded-lg" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white p-6 rounded-b-lg">
                            <p className="text-center font-medium">Device key location on package</p>
                        </div>
                        <button onClick={handleCloseModal} className="absolute top-4 right-4 w-8 h-8 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center text-white transition-all duration-200" aria-label="Close image">
                            <Icons.Close className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            <div className="flex gap-4 mb-8">
                <Button variant={ButtonVariants.Secondary} onClick={onBack} disabled={isProvisioning} className="flex-1 bg-white border border-warmGray-300 hover:border-warmGray-400 text-neutral-700 font-semibold rounded-xl transition-all duration-200 hover:bg-warmGray-50 disabled:opacity-50">
                    Back
                </Button>
                <Button onClick={handleProvisionDevice} disabled={!deviceKey.trim() || isProvisioning} className="flex flex-1 justify-center items-center bg-brand-primary hover:bg-brand-primary-darkened text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed">
                    {isProvisioning ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Provisioning Device...
                        </>
                    ) : (
                        <>
                            <Icons.Success className="w-4 h-4 mr-2" />
                            Add Device
                        </>
                    )}
                </Button>
            </div>

            <div className="bg-gradient-to-r from-terracotta-50 to-warmGray-50 border border-brand-primary border-opacity-20 rounded-xl p-6">
                <div className="flex items-start">
                    <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                        <Icons.QuestionMarkCircleOutline className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-neutral-900 mb-3">Need Help?</h4>
                        <div className="text-sm text-neutral-600 space-y-3 leading-relaxed">
                            <p>
                                <span className="font-medium text-neutral-700">Device key not working?</span> Make sure you've entered it exactly as shown, including any dashes or special characters.
                            </p>
                            <p>
                                <span className="font-medium text-neutral-700">Device already in use?</span> Contact support if the device key is already associated with another location.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
